import { useSelector } from 'react-redux';

// RBAC permission layer (frontend-first). Reads the resolved permissions object
// the backend delivers in `auth.user.permissions`:
//   { full: true }                                — owner/admin or no custom role
//   { full: false, modules, actions, hiddenData } — restricted by a custom role
//
// Defensive default: a missing/undefined permissions object (e.g. a login cached
// before this feature) is treated as FULL access — never lock a user out; the
// next getMe refresh delivers the real permissions.

// Build the helper API from a permissions object. Pure — usable outside React.
// `role` — ixtiyoriy, faqat owner/admin-only alohida qoidalar uchun kerak
// (masalan sklad-material-report) — boshqa hamma joyda ishlatilmaydi.
export function makePermissions(perm, role) {
  const full = !perm || perm.full !== false;
  const isSuperAdmin = ['owner', 'admin'].includes(role);
  const modules = Array.isArray(perm?.modules) ? perm.modules : [];
  const actions = perm?.actions && typeof perm.actions === 'object' ? perm.actions : {};
  const hiddenData = Array.isArray(perm?.hiddenData) ? perm.hiddenData : [];
  // Job order status filtri: null = hammasi, array = faqat shu statuslar
  const jobOrderStatuses = !full && Array.isArray(perm?.jobOrderStatuses) ? perm.jobOrderStatuses : null;
  // Kirim/chiqim to'lovlarda ruxsat etilgan kassalar: bo'sh array = cheklovsiz.
  const cashboxesIncoming = !full && Array.isArray(perm?.cashboxesIncoming) ? perm.cashboxesIncoming : [];
  const cashboxesOutgoing = !full && Array.isArray(perm?.cashboxesOutgoing) ? perm.cashboxesOutgoing : [];

  return {
    full,
    // Can this sidebar module / submodule be shown? `dashboard` always visible.
    // `sklad-material-report` — ATAYIN teskari standart: modul/rol ruxsatlaridan
    // MUSTAQIL, faqat owner/admin ko'radi (rolsiz oddiy foydalanuvchiga ham
    // ko'rinmaydi — boshqa ko'pchilik joydagi "rol yo'q = cheklovsiz" qoidasi
    // bu yerga TATBIQ ETILMAYDI, chunki material otchyot maxfiy hisoblanadi).
    canSeeModule: (navKey) => {
      if (navKey === 'sklad-material-report') return isSuperAdmin;
      return full || navKey === 'dashboard' || modules.includes(navKey);
    },
    // Is an action (view|create|edit|delete) allowed for a module?
    // Default-allow `view` so a visible module is at least readable.
    can: (navKey, action) => {
      if (full) return true;
      const a = actions[navKey];
      if (!a) return action === 'view';
      return !!a[action];
    },
    // Like `can`, but does NOT auto-allow when `full` is true (owner/admin OR a
    // plain user with no custom role assigned — both report full:true). Use this
    // for sensitive per-action overrides (masalan pul so'rovini tasdiqlash/to'lash)
    // that must be explicitly granted via a custom role, never implied by "no role
    // assigned yet".
    canExplicit: (navKey, action) => !full && !!actions[navKey]?.[action],
    // Should a sensitive data field be hidden?
    isHidden: (dataKey) => !full && hiddenData.includes(dataKey),
    // Job order status tab ni ko'rish mumkinmi? null = cheklov yo'q.
    canSeeJobOrderStatus: (status) => !jobOrderStatuses || jobOrderStatuses.includes(status),
    jobOrderStatuses,
    // To'lov yo'nalishi (incoming/outgoing) bo'yicha ruxsat etilgan kassa id'lari.
    // Bo'sh array = cheklovsiz (hammasi ruxsat).
    canUseCashbox: (dir, cashboxId) => {
      if (full) return true;
      const list = dir === 'incoming' ? cashboxesIncoming : cashboxesOutgoing;
      return !list.length || list.includes(String(cashboxId));
    },
    cashboxesIncoming,
    cashboxesOutgoing,
  };
}

// React hook — binds to the current auth user's permissions.
export function usePermissions() {
  const perm = useSelector((s) => s.auth.user?.permissions);
  const role = useSelector((s) => s.auth.user?.role);
  return makePermissions(perm, role);
}
